import React from 'react';
import pt from 'prop-types';
import { connect } from 'react-redux';
import isEqual from 'lodash/isEqual';
import pickBy from 'lodash/pickBy';
import actions from 'partners/actions';
import ViewLoading from 'components/view/loading';
import NotFoundPage from 'components/pages/error/404';
import Users from 'partners/components/pages/settings/users';
import User from 'partners/components/pages/settings/user';

const mapStateToProps = (state) => ({
  partner: state.partner,
});

const mapDispatchToProps = (dispatch) => ({
  fetchUser() {
    return dispatch(actions.user.fetch());
  },

  fetchUsers: () => {
    return dispatch(actions.user.members.fetch());
  },

  updateUserSelf: (values) => {
    return dispatch(actions.user.update(values));
  },

  updateMember: (id, values) => {
    return dispatch(actions.user.members.update(id, values));
  },

  updatePassword: (password) => {
    return dispatch(actions.user.updatePassword(password));
  },

  inviteUser: (values) => {
    return dispatch(actions.user.members.invite(values));
  },

  resendInvitation: (id) => {
    return dispatch(actions.user.members.resendInvitation(id));
  },

  removeUser: (id) => {
    return dispatch(actions.user.members.remove(id));
  },

  transferOwnership: (userId) => {
    return dispatch(actions.partner.transferOwnership(userId));
  },
});

export class UserSettings extends React.PureComponent {
  static propTypes = {
    params: pt.object,
    route: pt.object,
    fetchUser: pt.func,
    fetchUsers: pt.func,
    updateUserSelf: pt.func,
    updateMember: pt.func,
    updatePassword: pt.func,
    inviteUser: pt.func,
    resendInvitation: pt.func,
    removeUser: pt.func,
    transferOwnership: pt.func,
  };

  static contextTypes = {
    user: pt.object.isRequired,
    notifyError: pt.func.isRequired,
    notifySuccess: pt.func.isRequired,
    openModal: pt.func.isRequired,
    uploadImages: pt.func.isRequired,
  };

  constructor(props, context) {
    super(props, context);
    this.state = {
      edited: false,
      loaded: false,
      users: null,
      values: {},
      onSubmitUser: this.onSubmitUser.bind(this),
      onRemoveUser: this.onRemoveUser.bind(this),
      onInviteUser: this.onInviteUser.bind(this),
      onResendInvitation: this.onResendInvitation.bind(this),
      onTransferOwnership: this.onTransferOwnership.bind(this),
      onChangePassword: this.onChangePassword.bind(this),
      onUploadPhoto: this.onUploadPhoto.bind(this),
    };
  }

  componentDidMount() {
    this.fetchSettings();
  }

  componentDidUpdate(prevProps) {
    const { section } = this.props.route;
    const { section: prevSection } = prevProps.route;

    if (section && section !== prevSection) {
      this.setState({ loaded: false });
      this.fetchSettings();
    }
  }

  async fetchSettings() {
    const { fetchUsers } = this.props;

    const users = await fetchUsers();

    const settings = { users };

    this.setState({
      ...settings,
      values: { ...settings },
      loaded: true,
    });
  }

  getUser(userId) {
    const { users } = this.state;

    return users.results.find((user) => user.id === userId);
  }

  handleResult(result) {
    const { notifyError } = this.context;

    if (!result) {
      return false;
    } else if (result.error || result.errors) {
      notifyError(result.error || result.errors);
      return false;
    }

    return true;
  }

  async onSubmitUser(values) {
    const { id } = this.props.params;
    const { notifySuccess, user } = this.context;
    const { fetchUser, updateUserSelf, updateMember } = this.props;
    const isSelf = user.id === id;

    // Member fields are read-only in the portal, so the non-self form
    // cannot submit
    if (!isSelf) {
      return false;
    }

    // `notify_updates` lives on this partner's authorization, so it is
    // written through the team route rather than the user record
    const { notify_updates: notifyUpdates, ...profile } = values;
    const member = this.getUser(id);

    if (
      notifyUpdates !== undefined &&
      Boolean(notifyUpdates) !== Boolean(member?.notify_updates)
    ) {
      const memberResult = await updateMember(id, {
        notify_updates: Boolean(notifyUpdates),
      });

      if (!this.handleResult(memberResult)) {
        return false;
      }
    }

    // Only the fields that differ from the loaded member are written
    const changedProfile = pickBy(
      profile,
      (value, key) => !isEqual(value, member?.[key]),
    );

    if (Object.keys(changedProfile).length > 0) {
      const result = await updateUserSelf(changedProfile);

      if (!this.handleResult(result)) {
        return false;
      }
    }

    await fetchUser();
    await this.fetchSettings();

    notifySuccess('User profile updated');
  }

  removeUser = async (userId) => {
    const { notifySuccess } = this.context;
    const { removeUser } = this.props;

    const result = await removeUser(userId);

    if (!this.handleResult(result)) {
      return false;
    }

    notifySuccess('The user has been successfully removed from the account');

    await this.fetchSettings();

    return true;
  };

  onRemoveUser(userId) {
    const { openModal } = this.context;
    const user = this.getUser(userId);

    if (!user) {
      return;
    }

    return new Promise((resolve) => {
      openModal('ConfirmDelete', {
        title: `${user.name || user.email} user`,
        onConfirm: async () => {
          const result = await this.removeUser(userId);
          resolve(result);
        },
      });
    });
  }

  async onInviteUser(values) {
    const { notifySuccess } = this.context;
    const { inviteUser } = this.props;
    const { email } = values;

    const result = await inviteUser(values);

    if (!this.handleResult(result)) {
      return false;
    }

    notifySuccess(`Invite sent to ${email}`);

    await this.fetchSettings();

    return true;
  }

  async onResendInvitation(userId) {
    const { notifySuccess } = this.context;
    const { resendInvitation } = this.props;
    const user = this.getUser(userId);

    if (!user) {
      return;
    }

    const result = await resendInvitation(userId);

    if (!this.handleResult(result)) {
      return false;
    }

    notifySuccess(`Invite sent to ${user.email}`);
  }

  async transferOwnership(userId) {
    const { notifySuccess } = this.context;
    const { transferOwnership } = this.props;

    const result = await transferOwnership(userId);

    if (!this.handleResult(result)) {
      return false;
    }

    notifySuccess('Ownership has been successfully transferred');

    await this.fetchSettings();

    return true;
  }

  onTransferOwnership(userId) {
    const { openModal } = this.context;

    openModal('Confirm', {
      title: 'Transfer ownership',
      onConfirm: () => this.transferOwnership(userId),
    });
  }

  async onChangePassword(password) {
    const { updatePassword } = this.props;
    const result = await updatePassword(password);

    if (!this.handleResult(result)) {
      return false;
    }

    await this.fetchSettings();

    return true;
  }

  async onUploadPhoto(file) {
    const { uploadImages } = this.context;
    const results = await uploadImages([file]);
    const photo = results?.[0];

    if (!photo) {
      return;
    }

    this.onSubmitUser({ photo });
  }

  renderPage() {
    const { section } = this.props.route;

    switch (section) {
      case 'users':
        return <Users {...this.props} {...this.state} />;
      case 'user':
        return <User {...this.props} {...this.state} />;
      default:
        return <NotFoundPage />;
    }
  }

  render() {
    if (!this.state.loaded) {
      return <ViewLoading />;
    }

    return this.renderPage();
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(UserSettings);
